import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchApplications } from "../../../store/reducers/applicationSlice";
import Navbar from "../../navbar";
import Header from "../../header";
import Pagination from "../dashboard/appPagination";
import iconSearch from "../../../assets/media/icon-search.svg";
import DatePicker from "react-datepicker";
import { AiOutlineEye } from "react-icons/ai";
import { useNavigate } from "react-router-dom";
import iconMenuDots from "../../../assets/media/icon-menu-dots.svg";
import { Link } from "react-router-dom";
import { showSuccess, showError } from '../../../helpers/toast';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";

const columnHelper = createColumnHelper();

const AppDiscrepanciesCompany = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.user);
  const authToken = user.access_token;

  const applications = useSelector((state) => state.applications);
  const getallUserdata = useSelector((state) => state.userdata.getalluserdata);
  const [isDownloading, setIsDownloading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [filteredApplication, setFilteredApplication] = useState([]);
  const [downloadingIds, setDownloadingIds] = useState([]);
  const [dateRange, setDateRange] = useState([null, null]);
  const [startDate, endDate] = dateRange;
  const [modalNote, setModalNote] = useState("");
  const [showModal, setShowModal] = useState(false);

  // Fetch applications when date filter changes - fetch both statuses
  useEffect(() => {
    const fetchBothStatuses = async () => {
      let baseParams = {};
      if (startDate && endDate) {
        baseParams.start_date = startDate.toISOString();
        baseParams.end_date = endDate.toISOString();
      }

      const finalParams = {
        ...baseParams,
        state: "application_sent_to_discrepancy_by_company,dicrepancy_application_assigned_to_comapany_user"
      };

      // SECURITY: Add company_id filter at API level for company_admin
      if (user.user?.user_role === "company_admin" && user.user?.company_id) {
        finalParams.company_id = user.user.company_id;
      }
      // SECURITY: Add user_id filter at API level for employees
      else if (user.user?.user_role === "employee" && user.user?.id) {
        finalParams.user_id = user.user.id;
      }

      console.log("🔄 Fetching applications with both statuses using comma-separated approach");
      console.log("📋 API call parameters:", finalParams);
      console.log("🌐 Expected API URL:", `${process.env.REACT_APP_BACKEND_URL || 'API_URL'}/applications/v2/all?state=${finalParams.state}`);
      console.log("🔑 Auth token:", authToken ? 'Present' : 'Missing');

      try {
        // OPTIMAL APPROACH: Fetch all data once for client-side pagination
        // Since we have 46 applications, we need to fetch all of them
        // Use fetchAllPages: true to ensure we get all data, not just first page
        finalParams.per_page = 100; // Set high limit to fetch all in one request if possible

        const result = await dispatch(
          fetchApplications({
            accessToken: authToken,
            queryParams: finalParams,
            fetchAllPages: true // Fetch all pages to get all 46 applications
          })
        );

        console.log('Fetched discrepancy applications:', {
          count: result?.payload?.applications?.length || 0,
          total: result?.payload?.total || 0,
          totalPages: result?.payload?.totalPages || 1,
          note: 'All data fetched for client-side pagination'
        });
      } catch (error) {
        console.error('Error fetching discrepancy applications:', error);
      }
    };

    if (authToken) {
      fetchBothStatuses();
    } else {
      console.warn("⚠️ No auth token available, skipping fetch");
    }
    // Remove currentPage from dependencies - we fetch all data once, paginate client-side
    // Only refetch when filters change (date, role, company), not when page changes
  }, [authToken, dispatch, startDate, endDate, user.user?.user_role, user.user?.company_id, user.user?.id]);

  // Merge and Filter Data
  useEffect(() => {


    if (applications.data?.length > 0) {
      // Debug: Check what states are in the data
      const statesInData = [...new Set(applications.data.map((app) => app.state))];
      console.log("States in applications data:", statesInData);

      const mergedData = applications.data.map((appItem) => {
        const userIdFromApp = appItem.user_id || appItem.userId || appItem.user?.id;
        const userItem = getallUserdata?.find(
          (userData) => userData.id === userIdFromApp
        );
        const finalUserId = userItem?.id || userIdFromApp;
        // Preserve original company_id from application, don't let userItem overwrite it
        const finalCompanyId = appItem.company_id || appItem.companyId || userItem?.company_id || user.user?.company_id;

        if (userItem) {
          return {
            ...appItem,
            applicationId: appItem.id,
            ...userItem,
            user_id: finalUserId, // Preserve original user_id from application
            userId: userItem.id,
            company_id: finalCompanyId, // Preserve original company_id from application
          };
        }
        return {
          ...appItem,
          applicationId: appItem.id,
          user_id: finalUserId, // Preserve original user_id from application
          company_id: finalCompanyId, // Preserve original company_id from application
        };
      });




      // SECURITY: Filter by role, company, and state
      const companyAndStateFiltered = mergedData.filter((item) => {
        // Check for both states that we're fetching from API
        const hasCorrectState =
          item.state === "application_sent_to_discrepancy_by_company" ||
          item.state === "dicrepancy_application_assigned_to_comapany_user";
        if (!hasCorrectState) return false; // Early exit if state doesn't match

        const userRole = user.user?.user_role;
        const currentUserId = user.user?.id;
        const userCompanyId = user.user?.company_id;
        const itemUserId = item.user_id || item.userId || item.user?.id;
        const itemCompanyId = item.company_id || item.companyId;

        // SECURITY: If company_id is missing, don't show the application
        if (!itemCompanyId || !userCompanyId) {
          console.warn('Missing company_id - filtering out item:', {
            itemId: item.id,
            itemCompanyId,
            userCompanyId
          });
          return false;
        }

        let shouldShow = false;

        if (userRole === "company_admin") {
          // Company admin can see all applications in their company
          const hasCorrectCompany = String(itemCompanyId) === String(userCompanyId);
          shouldShow = hasCorrectCompany;
        } else if (userRole === "employee") {
          // Employee can only see their own applications within their company
          const isOwnApplication = String(itemUserId) === String(currentUserId);
          const hasCorrectCompany = String(itemCompanyId) === String(userCompanyId);
          shouldShow = isOwnApplication && hasCorrectCompany;
        } else {
          // Default: filter by company (fallback)
          const hasCorrectCompany = String(itemCompanyId) === String(userCompanyId);
          shouldShow = hasCorrectCompany;
        }

        console.log('Filtering discrepancy item:', {
          id: item.id,
          itemCompanyId,
          userCompanyId,
          itemUserId,
          currentUserId,
          state: item.state,
          userRole,
          shouldShow
        });

        return shouldShow;
      });

      // Then apply search filter
      const filteredData = companyAndStateFiltered.filter((item) => {
        if (!searchQuery) return true; // Show all if no search query
        return Object.values(item).some(
          (value) =>
            value &&
            value.toString().toLowerCase().includes(searchQuery.toLowerCase())
        );
      });

      console.log("Discrepancies - Filtered data summary:", {
        originalData: applications.data?.length,
        mergedData: mergedData?.length,
        companyAndStateFiltered: companyAndStateFiltered.length,
        filteredData: filteredData?.length,
        searchQuery,
        companyId: user.user.company_id,
        statesInData
      });

      setFilteredApplication(filteredData);
      // Only reset to page 1 when search query changes or filters change, not when pagination changes
      // This allows pagination to work correctly when user clicks different pages
    } else {
      // If no data, clear filtered applications
      setFilteredApplication([]);
    }
  }, [searchQuery, applications.data, getallUserdata, user.user.company_id, user.user?.user_role, user.user?.id]);

  // Reset to page 1 only when search query or date filter changes (not when pagination changes)
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, startDate, endDate]);

  // Pagination data - client-side pagination on already-fetched data
  const paginatedData = useMemo(() => {
    // Create a copy before sorting to avoid mutating the original array
    const sorted = [...filteredApplication].sort((a, b) => (b.applicationId || 0) - (a.applicationId || 0));
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const sliced = sorted.slice(startIndex, endIndex);

    console.log('Pagination calculation:', {
      currentPage,
      itemsPerPage,
      filteredApplicationLength: filteredApplication.length,
      startIndex,
      endIndex,
      slicedLength: sliced.length,
      totalPages: Math.ceil(filteredApplication.length / itemsPerPage),
      showingItems: sliced.map(item => item.applicationId)
    });

    return sliced;
  }, [filteredApplication, currentPage, itemsPerPage]);

  // Get total pages from filtered data (client-side pagination)
  const getTotalPages = () => {
    const total = Math.ceil(filteredApplication.length / itemsPerPage);
    console.log('getTotalPages called:', {
      filteredApplicationLength: filteredApplication.length,
      itemsPerPage,
      totalPages: total
    });
    return total || 1; // Always return at least 1 page
  };

  // Download
  const handleDownload = useCallback(
    async (item) => {
      const { applicationId, userId } = item;

      // Prevent multiple clicks
      if (isDownloading || downloadingIds.includes(applicationId)) {
        return;
      }

      setDownloadingIds((prev) => {
        const newIds = [...prev, applicationId];
        return newIds;
      });
      setIsDownloading(true);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}file/${userId}/downloadapplicationfiles/${applicationId}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/zip",
              Authorization: `Bearer ${authToken}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error(`Download failed: ${response.status} ${response.statusText}`);
        }

        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `application_${applicationId}_files.zip`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        showSuccess('Files downloaded successfully!');
      } catch (error) {
        showError(`Download failed: ${error.message}`);
      } finally {
        setDownloadingIds((prev) => {
          const newIds = prev.filter((id) => id !== applicationId);
          return newIds;
        });
        setIsDownloading(false);
      }
    },
    [authToken, downloadingIds, isDownloading]
  );

  // React Table columns
  const columns = useMemo(
    () => [
      columnHelper.display({
        id: "serial",
        header: "S.NO.",
        cell: (info) => info.row.index + 1,
      }),
      columnHelper.accessor("applicationId", {
        header: "Application No.",
        cell: (info) => `GB-${info.getValue()}`,
      }),
      columnHelper.display({
        id: "applicationName",
        header: "Application Name",
        cell: (info) => {
          const row = info.row.original;
          return (
            <div className="boardTitle">
              <strong>{row.name}</strong>
              <span>{row.categoryName}</span>
            </div>
          );
        },
      }),
      columnHelper.display({
        id: "submittedFor",
        header: "Submitted By",
        cell: (info) => {
          const row = info.row.original;

          return (
            <div className="boardTitle">
              <strong>{row.companyName || row.company_name || 'N/A'}</strong>
              <span>{row.companyEmail || row.email || 'N/A'}</span>
            </div>
          );
        },
      }),
      columnHelper.display({
        id: "submittedFor",
        header: "Submitted For",
        cell: (info) => {
          const row = info.row.original;
          return (
            <div className="boardTitle">
              <strong>{row.userName || row.user_name || row.name || 'N/A'}</strong>
              <span>{row.userEmail || row.user_email || row.email || 'N/A'}</span>
            </div>
          );
        },
      }),
      columnHelper.accessor("updated_at", {
        header: "Last Updated",
        cell: (info) => (
          <span className="dateText">{info.getValue()?.slice(0, 10)}</span>
        ),
      }),
      columnHelper.accessor("state", {
        header: "Status",
        cell: (info) => <span className="dateText">{info.getValue() === "dicrepancy_application_assigned_to_comapany_user" ? "Assigned to Employee" : "Assigned to Company"}</span>,
      }),
      columnHelper.accessor("note", {
        header: "Note",
        cell: (info) => {
          const row = info.row.original;
          // Check for note in both text_dis_form1_1 and dis_form1_1 (with object .value), and fallback to dis_form2_1.value or ""
          let note = "";
          if (row.discrepancy?.text_dis_form1_1 !== undefined && row.discrepancy?.text_dis_form1_1 !== null && row.discrepancy?.text_dis_form1_1 !== "") {
            note = row.discrepancy.text_dis_form1_1;
          } else if (row.discrepancy?.dis_form1_1 && typeof row.discrepancy.dis_form1_1 === "object" && row.discrepancy.dis_form1_1.value) {
            note = row.discrepancy.dis_form1_1.value;
          } else if (row.discrepancy?.dis_form2_1 && typeof row.discrepancy.dis_form2_1 === "object" && row.discrepancy.dis_form2_1.value) {
            note = row.discrepancy.dis_form2_1.value;
          }
          
          if (!note) {
            return <span className="dateText" style={{ color: "#888" }}>No note</span>;
          }

          return (
            <span
              className="dateText"
              style={{ color: "#007bff", cursor: "pointer", textDecoration: "none" }}
              onClick={() => {
                setModalNote(note);
                setShowModal(true);
              }}
            >
              {note.length > 30 ? note.slice(0, 30) + "..." : note}
            </span>
          );
        },
      }),
      columnHelper.display({
        id: "actions",
        header: "Actions",
        cell: (info) => {
          const row = info.row.original;
          return (
            <div className="boardActions">
              <div className="dropdown d-inline-flex">
                <button
                  className="application-dropdown dropdown-toggle border-0 bg-transparent"
                  type="button"
                  id="dropdownMenuButton"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  <img src={iconMenuDots} alt="Menu Dots" />
                </button>
                <ul
                  className="dropdown-menu"
                  aria-labelledby="dropdownMenuButton"
                >
                  <li>
                    <Link
                      onClick={() => {
                        if (!isDownloading && !downloadingIds.includes(row.applicationId)) {
                          handleDownload(row);
                        }
                      }}
                      className="dropdown-item"
                      style={{
                        opacity: isDownloading || downloadingIds.includes(row.applicationId) ? 0.6 : 1,
                        pointerEvents: isDownloading || downloadingIds.includes(row.applicationId) ? 'none' : 'auto',
                        cursor: isDownloading || downloadingIds.includes(row.applicationId) ? 'not-allowed' : 'pointer'
                      }}
                    >
                      {isDownloading || downloadingIds.includes(row.applicationId) ? 'Downloading...' : 'Download'}
                    </Link>
                  </li>
                  <li>
                    {user.user.user_role === "company_admin" && row.state === "application_sent_to_discrepancy_by_company" && (
                      <Link to={`/view-discrepancies/company/${row.applicationId}`} className="dropdown-item">
                        View and Edit
                      </Link>
                    )}
                    {user.user.user_role === "employee" && row.state === "dicrepancy_application_assigned_to_comapany_user" && (
                      <Link to={`/view-discrepancies/company/${row.applicationId}`} className="dropdown-item">
                        View and Edit
                      </Link>
                    )}
                  </li>
                  {/* <li>
                    <Link
                      onClick={() =>
                        navigate(`/view-discrepancies/company/${row.applicationId}`)
                      }
                      className="dropdown-item"
                    >
                      Assign to Company
                    </Link>
                  </li> */}
                </ul>
              </div>
            </div >
          );
        },
      }),
    ],
    [downloadingIds, currentPage, itemsPerPage, handleDownload, navigate, getallUserdata]
  );

  const table = useReactTable({
    data: paginatedData,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (applications.fetchLoading) {
    return (
      <div className="mainApp">
        <Navbar />
        <Header />
        <div className="wrapper">
          <div className="boxAppBoard boxWhite">
            <div className="text-center p-4">
              <div className="spinner"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }



  return (
    <div className="mainApp">
      <Navbar />
      <Header />
      <div className="wrapper">
        <div className="boxAppBoard boxWhite">
          {/* Header with search + date filter */}
          <div className="boardHeader">
            <div className="boardHeadLeft">
              <h2>Discrepancies Company</h2>
            </div>
            <div className="boardHeadRight">
              <form className="searchForm">
                <input
                  type="text"
                  placeholder="Search"
                  onChange={(e) => setSearchQuery(e.target.value)}
                  value={searchQuery}
                  className="searchInput"
                />
                <button type="button" className="searchBtn">
                  <img src={iconSearch} alt="search" />
                </button>
              </form>
              <div className="datePicker">
                <DatePicker
                  selectsRange={true}
                  startDate={startDate}
                  endDate={endDate}
                  onChange={(update) => setDateRange(update)}
                  isClearable={true}
                  placeholderText="Select Date Range"
                  className="date-range-input"
                  dateFormat="yyyy/MM/dd"
                />
              </div>
            </div>
          </div>

          {/* Table Content */}
          <div className="boardContent table-responsive">
            <table className="table">
              <thead className="">
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr className="" key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th key={header.id}>{flexRender(header.column.columnDef.header, header.getContext())}</th>
                    ))}
                  </tr>
                ))}
              </thead>

              <tbody>
                {table.getRowModel().rows.length > 0 ? (
                  table.getRowModel().rows.map((row) => (
                    <tr className="" key={row.id}>
                      {row.getVisibleCells().map((cell) => (
                        <td key={cell.id}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={table.getHeaderGroups()?.[0]?.headers.length}
                      style={{
                        textAlign: "center",
                        padding: "20px",
                      }}
                    >
                      {applications.fetchLoading ? (
                        <p>Loading applications...</p>
                      ) : applications.fetchError ? (
                        <div>
                          <p style={{ color: 'red' }}>Error loading applications: {applications.fetchError}</p>
                        </div>
                      ) : (
                        <div>
                          <p>No applications found</p>
                        </div>
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <Pagination
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            totalPages={getTotalPages}
            itemsPerPage={itemsPerPage}
            setItemsPerPage={setItemsPerPage}
          />
        </div>
      </div>
    </div >
  );
};

export default AppDiscrepanciesCompany;
